import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useStore } from '../../../../../models'
import { ChatKind } from '../../../../../models/api-store'
import { INPUT_HEIGHT } from './partials/ChatTextInput'
import { useHandHistoryQuery } from '@/hooks/convex/useGameState'
import { useGameContext } from '@/screens/GameContainer/GameContext'
import { buildWinnersDistributionMessage } from './ChatWinner'
import { sortBy } from 'lodash'
import { useChatMessages } from '@/hooks/convex/useChatMessages'
import { Doc } from '@ppwr/convex/_generated/dataModel'
import { isCelebrationMessage, parseCelebrationMessage } from '../../../../../screens/new-design/hooks/useCelebration'
import { ChatMessagesList } from './ChatMessagesList'

// LEAVE for re-test after full multiplayer work
// import {
//   mockChatMessages,
//   mockCompletedHand1,
//   mockCompletedHand2,
//   mockCompletedSidePots1
// } from '../../../../../utils/mockHandHistory'
// mockCompletedHand1._creationTime = mockChatMessages[1].timestamp + 1000
// mockCompletedHand2._creationTime = mockChatMessages[1].timestamp + 2000
// mockCompletedSidePots1._creationTime = mockChatMessages[1].timestamp + 3000
// const allMocks = [mockCompletedHand1, mockCompletedHand2, mockCompletedSidePots1]

// TODO revisit this later
const DEFAULT_CHAT_MESSAGE_LIMIT = 20

const convertConvexToLegacyChat = (msg: Doc<'chatMessages'>) => {
  const converted = {
    message: msg.message,
    chatKind: ChatKind[msg.chatKind || 'dealer'],
    timestamp: msg._creationTime,
    userName: msg.userName,
    _id: msg._id,
    channelName: msg.gameId,
    color: msg.color,
    userId: ''
  }
  if (msg.chatKind === ChatKind.engine && isCelebrationMessage(msg.message)) {
    const emoji = parseCelebrationMessage(msg.message)
    converted.message = `${msg.userName || 'Someone'} sent ${emoji} ${emoji}`
  }

  return converted
}

const FOOTER_VERTICAL_PADDING = 16

interface ChatContentProps {
  clearanceOverride?: number
  listHeight?: number
}

export const ChatContent = observer((props: ChatContentProps) => {
  const insets = useSafeAreaInsets()
  const bottomInset = Math.max(insets.bottom, 16)
  const { height: windowHeight } = useWindowDimensions()
  const collapsedSheetHeight = Math.floor(windowHeight * 0.5)
  const footerHeight = INPUT_HEIGHT + FOOTER_VERTICAL_PADDING + bottomInset * 2
  const footerClearance = Math.max(footerHeight, collapsedSheetHeight * 0.35)

  const {
    communication: { channelChats }
  } = useStore()
  const legacyMessages = useMemo(() => channelChats ?? [], [channelChats])
  // LEAVE for re-test after full multiplayer work
  // const legacyMessages = useMemo(() => mockChatMessages, [mockChatMessages]) // for testing long messages

  const { gameId } = useGameContext()
  const { messages: convexMessages } = useChatMessages(gameId, DEFAULT_CHAT_MESSAGE_LIMIT)
  const { handHistory } = useHandHistoryQuery(gameId)
  // LEAVE for re-test after full multiplayer work
  // const handHistory = useMemo(() => [allMocks], [...allMocks])

  const allMessages = useMemo(() => {
    const reverseSortedHandHistory = handHistory ? [...handHistory].reverse() : []
    const reversedHandHistoryMessages = reverseSortedHandHistory.map((hand) => ({
      message: buildWinnersDistributionMessage(hand),
      chatKind: ChatKind.engine,
      timestamp: hand._creationTime,
      userName: '',
      _id: hand._id,
      channelName: undefined,
      color: undefined,
      userId: ''
    }))

    const convertedConvexMessages = convexMessages?.map(convertConvexToLegacyChat) || []

    // Note: could just be a merge sort, both arrays already sorted, but lists are so short and they're memoized
    const sortedMessages = sortBy(
      [...legacyMessages, ...convertedConvexMessages, ...reversedHandHistoryMessages],
      'timestamp'
    )
    return sortedMessages
  }, [handHistory, legacyMessages, convexMessages])

  const { clearanceOverride, listHeight } = props
  const clearance = undefined !== clearanceOverride ? clearanceOverride : footerClearance
  return <ChatMessagesList messages={allMessages} footerClearance={clearance} listHeight={listHeight} />
})
